/**
 * DaData API клиент (1.32.0)
 *
 * Используется для:
 *  • Автоподсказок адреса при вводе в CompanySetupModal.
 *  • Автоопределения часового пояса по выбранному адресу.
 *  • Сохранения координат адреса (lat/lon) на будущее (карты, маршруты).
 *
 * API: https://suggestions.dadata.ru/suggestions/api/4_1/rs/suggest/address
 *
 * Ключ: VITE_DADATA_API_KEY, прописывается в Vercel. Если ключ не задан —
 * функции тихо возвращают пустые массивы, без падений. Это позволяет
 * приложению работать в локальной разработке без ключа: админ сможет
 * ввести адрес вручную, просто без подсказок.
 *
 * Лимит на бесплатном тарифе DaData: 10000 запросов/сутки. С наших
 * объёмов (несколько компаний, несколько админов, ввод адреса при
 * настройке) — даже близко не подойдём.
 */

const DADATA_SUGGEST_URL =
  'https://suggestions.dadata.ru/suggestions/api/4_1/rs/suggest/address';

/** Сырой ответ DaData (важные поля). */
interface RawDaDataSuggestion {
  value: string;
  unrestricted_value: string;
  data: {
    geo_lat?: string | null;
    geo_lon?: string | null;
    timezone?: string | null;
    // Уровни иерархии адреса (для будущей фильтрации):
    country?: string | null;
    region?: string | null;
    city?: string | null;
    // Полный объект содержит ~70 полей, перечислять не нужно.
    [k: string]: unknown;
  };
}

interface DaDataResponse {
  suggestions: RawDaDataSuggestion[];
}

/** Нормализованная подсказка для UI. */
export interface AddressSuggestion {
  /** Краткая отображаемая строка ("г Москва, ул Ленина, д 8"). */
  value: string;
  /** Полная каноническая форма с индексом и регионом. */
  fullValue: string;
  /** Координаты, если DaData их вернула. */
  lat: number | null;
  lon: number | null;
  /**
   * IANA-таймзона ("Europe/Moscow"). DaData отдаёт строки вида
   * "UTC+3" или "Europe/Moscow" в зависимости от уровня адреса —
   * нормализуем к IANA через locale-таблицу ниже.
   */
  timezone: string | null;
}

/**
 * DaData возвращает таймзону в разных форматах ("UTC+3", "+3", иногда
 * пусто). Конвертируем в IANA. Для России полный набор основных зон.
 */
const RU_OFFSET_TO_IANA: Record<string, string> = {
  'UTC+2':  'Europe/Kaliningrad',
  'UTC+3':  'Europe/Moscow',
  'UTC+4':  'Europe/Samara',
  'UTC+5':  'Asia/Yekaterinburg',
  'UTC+6':  'Asia/Omsk',
  'UTC+7':  'Asia/Krasnoyarsk',
  'UTC+8':  'Asia/Irkutsk',
  'UTC+9':  'Asia/Yakutsk',
  'UTC+10': 'Asia/Vladivostok',
  'UTC+11': 'Asia/Magadan',
  'UTC+12': 'Asia/Kamchatka',
};

function normalizeTimezone(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  // Уже IANA — содержит слэш ("Europe/Moscow")
  if (trimmed.includes('/')) return trimmed;
  // "UTC+3", "+3", "UTC +3"
  const m = trimmed.match(/([+-]?\d{1,2})/);
  if (m) {
    const offset = parseInt(m[1], 10);
    const key = `UTC${offset >= 0 ? '+' : ''}${offset}`;
    return RU_OFFSET_TO_IANA[key] ?? null;
  }
  return null;
}

function parseFloatOrNull(s: string | null | undefined): number | null {
  if (!s) return null;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

/**
 * Получить подсказки по строке. Возвращает пустой массив при отсутствии
 * ключа, ошибке сети, пустом ответе.
 *
 * Вызывать после небольшого debounce (200-300 мс), чтобы не дёргать
 * DaData на каждом нажатии.
 */
export async function suggestAddress(
  query: string,
  options: { count?: number; signal?: AbortSignal } = {},
): Promise<AddressSuggestion[]> {
  const apiKey = (import.meta.env.VITE_DADATA_API_KEY as string | undefined)?.trim();
  if (!apiKey) {
    // Ключ не задан — работаем без подсказок. Это намеренное мягкое
    // поведение для локальной разработки и для случая, если ключ
    // временно недоступен.
    return [];
  }

  const q = query.trim();
  if (q.length < 3) return []; // меньше 3 символов — нет смысла дёргать

  try {
    const response = await fetch(DADATA_SUGGEST_URL, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Token ${apiKey}`,
      },
      body: JSON.stringify({
        query: q,
        count: options.count ?? 7,
        // Ограничиваем РФ — DaData по умолчанию ищет шире.
        locations: [{ country: '*' }],
      }),
      signal: options.signal,
    });

    if (!response.ok) {
      // 403 = неверный ключ; 429 = превышен лимит; что угодно — тихо
      // возвращаем пусто, не валим UX.
      return [];
    }

    const json = (await response.json()) as DaDataResponse;
    if (!json.suggestions) return [];

    return json.suggestions.map((s) => ({
      value: s.value,
      fullValue: s.unrestricted_value,
      lat: parseFloatOrNull(s.data?.geo_lat),
      lon: parseFloatOrNull(s.data?.geo_lon),
      timezone: normalizeTimezone(s.data?.timezone),
    }));
  } catch {
    // AbortError / network — тихо возвращаем пусто.
    return [];
  }
}
